import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_REGION_KEY,
  getRegionExperience,
  missionSiteFallback,
  normalizeMissionConfig,
} from "../data/missionContent";
import i18n from "../i18n/i18n";
import { localizedUrl } from "../lib/fetchLocalized";

const REGION_STORAGE_KEY = "agm_region";
// Stale-while-revalidate cache of the last successful CMS site-config. Seeding the
// initial render from this (instead of the hardcoded fallback) means returning
// visitors see the REAL banner immediately, with no "hardcoded -> actual" flash.
// Bump the version suffix if the normalized config shape changes.
const CONFIG_CACHE_KEY = "agm_site_config_v5";

function readCachedExperience() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONFIG_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.config) return parsed; // { config, region }
  } catch {
    /* corrupt/disabled storage — fall back to the bundled default */
  }
  return null;
}

function writeCachedExperience(config, region) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify({ config, region }));
  } catch {
    /* quota exceeded / storage disabled — non-fatal */
  }
}

function getConfiguredApiBase() {
  const configured =
    import.meta.env.VITE_APP_SITE_CONFIG_API_URL ||
    import.meta.env.VITE_APP_BASE_URL ||
    import.meta.env.VITE_API_BASE_URL ||
    "/api/nextjs";

  return configured.replace(/\/$/, "");
}

function normalizeRegion(value) {
  const upper = value?.toUpperCase();
  if (upper === "US" || upper === "USA") return "US";
  if (upper === "IN" || upper === "INDIA") return "IN";
  return null;
}

export function detectRegionalFallback() {
  if (typeof window === "undefined") return DEFAULT_REGION_KEY;

  const params = new URLSearchParams(window.location.search);
  const queryRegion = normalizeRegion(params.get("region"));
  if (queryRegion) {
    window.localStorage.setItem(REGION_STORAGE_KEY, queryRegion);
    return queryRegion;
  }

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  const language = window.navigator.language || "";

  if (timeZone === "Asia/Kolkata" || language.toLowerCase().includes("en-in")) return "IN";

  return "US";
}

export function persistRegion(regionKey) {
  const normalized = normalizeRegion(regionKey) || DEFAULT_REGION_KEY;
  if (typeof window !== "undefined") {
    window.localStorage.setItem(REGION_STORAGE_KEY, normalized);
  }
  return normalized;
}

export function useRegionalExperience() {
  // Seed from the last successful CMS fetch (localStorage) so returning visitors
  // see the real banner instantly; only a first-ever visitor sees the bundled
  // fallback. The background fetch below still runs to refresh the cache.
  const [regionKey, setRegionKey] = useState(
    () => readCachedExperience()?.region || detectRegionalFallback()
  );
  const [siteConfig, setSiteConfig] = useState(
    () => readCachedExperience()?.config || missionSiteFallback
  );
  const [isResolvingRegion, setIsResolvingRegion] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadSiteConfig() {
      const fallbackRegion = detectRegionalFallback();
      setRegionKey(fallbackRegion);

      try {
        const response = await fetch(
          localizedUrl(`${getConfiguredApiBase()}/site-config?region=${fallbackRegion}`),
          { credentials: "include" }
        );

        if (!response.ok) throw new Error("Site config unavailable");

        const payload = await response.json();
        if (cancelled) return;

        const nextConfig = normalizeMissionConfig(payload.data || payload);
        const resolvedRegion = normalizeRegion(payload.regionKey) || fallbackRegion;

        setSiteConfig(nextConfig);
        setRegionKey(resolvedRegion);
        persistRegion(resolvedRegion);
        writeCachedExperience(nextConfig, resolvedRegion);
      } catch (error) {
        if (!cancelled) {
          setSiteConfig(missionSiteFallback);
          setRegionKey(fallbackRegion);
        }
      } finally {
        if (!cancelled) setIsResolvingRegion(false);
      }
    }

    loadSiteConfig();
    i18n.on("languageChanged", loadSiteConfig);

    return () => {
      cancelled = true;
      i18n.off("languageChanged", loadSiteConfig);
    };
  }, []);

  const region = useMemo(() => getRegionExperience(siteConfig, regionKey), [regionKey, siteConfig]);

  const switchRegion = (nextRegion) => {
    const normalized = persistRegion(nextRegion);
    setRegionKey(normalized);
  };

  return {
    siteConfig,
    region,
    regionKey,
    switchRegion,
    isResolvingRegion,
  };
}
